import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft,
  ChevronRight,
  Bookmark,
  Images,
  Share2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import './styles.css';

interface VehicleGalleryProps {
  photos: string[];
  alt: string;
  isZeroKm?: boolean;
  isFavorited: boolean;
  onFavorite: () => void;
  onShare: () => void;
}

const GRID_INDICES = [1, 2, 3, 4] as const;

export function VehicleGallery({
  photos,
  alt,
  isZeroKm,
  isFavorited,
  onFavorite,
  onShare,
}: VehicleGalleryProps) {
  const { colors } = useTheme();
  const buttonColor = colors.button;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [isFullscreenLoading, setIsFullscreenLoading] = useState(false);
  const fullscreenStripRef = useRef<HTMLDivElement>(null);

  const total = photos.length;
  const hasMultiple = total > 1;
  const showMoreInGrid = total > 5;
  const moreCount = showMoreInGrid ? total - 5 : 0;

  const gridSlots = useMemo(() => {
    if (!hasMultiple) return [];

    return GRID_INDICES.map((photoIndex, slotIndex) => {
      if (slotIndex === 3 && showMoreInGrid) {
        return { type: 'more' as const, photoIndex: 4, moreCount };
      }
      if (photoIndex < total) {
        return { type: 'photo' as const, photoIndex };
      }
      return null;
    }).filter((slot): slot is { type: 'photo'; photoIndex: number } | { type: 'more'; photoIndex: number; moreCount: number } => slot !== null);
  }, [hasMultiple, moreCount, showMoreInGrid, total]);

  const openFullscreen = useCallback((index: number) => {
    setFullscreenIndex(index);
    setIsFullscreen(true);
  }, []);

  const closeFullscreen = useCallback(() => {
    setCurrentIndex(fullscreenIndex);
    setIsFullscreen(false);
  }, [fullscreenIndex]);

  const goFullscreenNext = useCallback(() => {
    if (isFullscreenLoading || !hasMultiple) return;
    setIsFullscreenLoading(true);
    setFullscreenIndex((prev) => (prev === total - 1 ? 0 : prev + 1));
  }, [hasMultiple, isFullscreenLoading, total]);

  const goFullscreenPrev = useCallback(() => {
    if (isFullscreenLoading || !hasMultiple) return;
    setIsFullscreenLoading(true);
    setFullscreenIndex((prev) => (prev === 0 ? total - 1 : prev - 1));
  }, [hasMultiple, isFullscreenLoading, total]);

  useEffect(() => {
    if (!isFullscreen) return;

    const thumb = fullscreenStripRef.current?.querySelector<HTMLElement>(
      `[data-strip-index="${fullscreenIndex}"]`
    );
    thumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [fullscreenIndex, isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) return;

    const scrollY = window.scrollY;
    const { style } = document.body;
    const previousOverflow = style.overflow;
    const previousPaddingRight = style.paddingRight;
    const previousPosition = style.position;
    const previousTop = style.top;
    const previousWidth = style.width;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    style.overflow = 'hidden';
    style.position = 'fixed';
    style.top = `-${scrollY}px`;
    style.width = '100%';
    if (scrollbarWidth > 0) {
      style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      style.overflow = previousOverflow;
      style.position = previousPosition;
      style.top = previousTop;
      style.width = previousWidth;
      style.paddingRight = previousPaddingRight;
      window.scrollTo(0, scrollY);
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFullscreen();
      if (e.key === 'ArrowRight') goFullscreenNext();
      if (e.key === 'ArrowLeft') goFullscreenPrev();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeFullscreen, goFullscreenNext, goFullscreenPrev, isFullscreen]);

  if (!total) return null;

  const heroBlock = (
    <div className="wl7-gallery__hero group">
      <img
        key={currentIndex}
        src={photos[currentIndex]}
        alt={alt}
        className="wl7-gallery__hero-img"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />

      <button
        type="button"
        onClick={() => openFullscreen(currentIndex)}
        className="wl7-gallery__hero-zoom"
        aria-label="Ampliar foto"
      />

      {isZeroKm && (
        <Badge className="absolute top-3 left-3 z-20 bg-white text-foreground shadow-sm border-0">
          Zero KM
        </Badge>
      )}

      <div className="wl7-gallery__hero-actions">
        <button
          type="button"
          onClick={onFavorite}
          className="wl7-gallery__action-btn"
          title={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
          <Bookmark
            className="h-[18px] w-[18px]"
            fill={isFavorited ? 'currentColor' : 'none'}
            style={isFavorited ? { color: buttonColor } : { color: 'var(--wl7-text-muted)' }}
          />
        </button>
        <button type="button" onClick={onShare} className="wl7-gallery__action-btn">
          <Share2 className="h-[18px] w-[18px] text-slate-600" />
        </button>
      </div>

      <div className="wl7-gallery__hero-footer">
        {hasMultiple && (
          <span className="wl7-gallery__counter">
            {currentIndex + 1} / {total}
          </span>
        )}
        <button
          type="button"
          onClick={() => openFullscreen(currentIndex)}
          className="wl7-gallery__expand"
          aria-label="Ver galeria em tela cheia"
        >
          <Images className="h-4 w-4" />
          Todas as fotos
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="wl7-gallery">
        <div className="wl7-gallery__frame">
          <div
            className={cn(
              'wl7-gallery__body',
              hasMultiple ? 'wl7-gallery__body--boxcar' : 'wl7-gallery__body--solo'
            )}
          >
            {heroBlock}

            {hasMultiple && gridSlots.length > 0 && (
              <div className="wl7-gallery__grid">
                {gridSlots.map((slot) => {
                  if (slot.type === 'more') {
                    return (
                      <button
                        key="more"
                        type="button"
                        onClick={() => openFullscreen(slot.photoIndex)}
                        className={cn(
                          'wl7-gallery__grid-thumb wl7-gallery__grid-thumb--more',
                          currentIndex >= slot.photoIndex && 'wl7-gallery__grid-thumb--active'
                        )}
                        aria-label={`Ver mais ${slot.moreCount} fotos`}
                      >
                        <img
                          src={photos[slot.photoIndex]}
                          alt=""
                          className="wl7-gallery__grid-thumb-img wl7-gallery__grid-thumb-img--dimmed"
                          loading="lazy"
                        />
                        <span className="wl7-gallery__grid-more-label" aria-hidden>
                          +{slot.moreCount}
                        </span>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={slot.photoIndex}
                      type="button"
                      onClick={() => openFullscreen(slot.photoIndex)}
                      className={cn(
                        'wl7-gallery__grid-thumb',
                        slot.photoIndex === currentIndex && 'wl7-gallery__grid-thumb--active'
                      )}
                      aria-label={`Abrir foto ${slot.photoIndex + 1}`}
                    >
                      <img
                        src={photos[slot.photoIndex]}
                        alt=""
                        className="wl7-gallery__grid-thumb-img"
                        loading="lazy"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {isFullscreen && (
        <div
          className="wl7-gallery-lightbox"
          role="dialog"
          aria-modal
          aria-label="Galeria de fotos"
          onClick={closeFullscreen}
        >
          <button
            type="button"
            onClick={closeFullscreen}
            className="wl7-gallery-lightbox__close"
            aria-label="Fechar galeria"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="wl7-gallery-lightbox__meta">
            <span className="wl7-gallery-lightbox__counter">
              {fullscreenIndex + 1} / {total}
            </span>
          </div>

          <div className="wl7-gallery-lightbox__stage">
            {isFullscreenLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                <div className="animate-spin rounded-full h-14 w-14 border-2 border-white/20 border-t-white" />
              </div>
            )}

            <img
              key={fullscreenIndex}
              src={photos[fullscreenIndex]}
              alt={alt}
              className={cn(
                'wl7-gallery-lightbox__img',
                isFullscreenLoading && 'opacity-40'
              )}
              onClick={(e) => e.stopPropagation()}
              onLoad={() => setIsFullscreenLoading(false)}
            />

            {hasMultiple && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goFullscreenPrev();
                  }}
                  disabled={isFullscreenLoading}
                  className="wl7-gallery-lightbox__nav wl7-gallery-lightbox__nav--prev"
                  aria-label="Foto anterior"
                >
                  <ChevronLeft className="h-7 w-7" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goFullscreenNext();
                  }}
                  disabled={isFullscreenLoading}
                  className="wl7-gallery-lightbox__nav wl7-gallery-lightbox__nav--next"
                  aria-label="Próxima foto"
                >
                  <ChevronRight className="h-7 w-7" />
                </button>
              </>
            )}
          </div>

          {hasMultiple && (
            <div className="wl7-gallery-lightbox__strip" onClick={(e) => e.stopPropagation()}>
              <div ref={fullscreenStripRef} className="wl7-gallery-lightbox__strip-inner">
                {photos.map((photo, index) => (
                  <button
                    key={index}
                    type="button"
                    data-strip-index={index}
                    onClick={() => {
                      if (index !== fullscreenIndex) setIsFullscreenLoading(true);
                      setFullscreenIndex(index);
                    }}
                    className={cn(
                      'wl7-gallery-lightbox__strip-thumb',
                      index === fullscreenIndex && 'wl7-gallery-lightbox__strip-thumb--active'
                    )}
                  >
                    <img src={photo} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
